import { useCallback, useState } from 'react';
import { logError } from '@zg-rentals/log-error';
import type { ListingDetails } from 'app/types/listingDetails.type';
import {
  emitTrackHdpCopyShareUrlEvent,
  emitTrackHdpTextShareEvent,
} from 'app/shared/models/Clickstream/HdpClickstreamEvents';
import { buildShareUrl } from 'app/shared/hooks/useShareAction';
import IconShareLink from 'images/icons/share-link.svg';
import IconShareSms from 'images/icons/share-sms.svg';
import IconShareFacebook from 'images/icons/share-facebook.svg';
import IconShareTwitter from 'images/icons/share-twitter.svg';
import { shareFormContainerCX, shareFormTitleCX, socialIconCX, socialItemCX, socialListCX } from './shareForm.styles';

type ShareFormProps = {
  listing: ListingDetails;
  triggerLocation?: string;
  onClose: () => void;
};

const SHARE_SUBJECT = 'Check out this listing I found on HotPads!';

const MessengerIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 2C6.477 2 2 6.145 2 11.243c0 2.908 1.194 5.425 3.14 7.102V22l3.468-1.903A11.27 11.27 0 0012 20.486c5.523 0 10-4.145 10-9.243S17.523 2 12 2zm.994 12.448l-2.55-2.72-4.98 2.72 5.474-5.81 2.613 2.72 4.916-2.72-5.473 5.81z"
      fill="#2F3C53"
    />
  </svg>
);

const WhatsAppIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.05 21.785h-.01a9.86 9.86 0 01-5.03-1.378l-.36-.214-3.742.981.999-3.648-.235-.374A9.848 9.848 0 012.2 12.05C2.2 6.624 6.624 2.2 12.05 2.2c2.623 0 5.088 1.023 6.94 2.879a9.82 9.82 0 012.877 6.94c-.003 5.427-4.427 9.855-9.857 9.855l.04-.09zM20.52 3.449C18.247 1.226 15.235 0 12.05 0 5.463 0 .104 5.36.102 11.947a11.92 11.92 0 001.595 5.972L0 24l6.233-1.635a11.93 11.93 0 005.71 1.454h.005c6.585 0 11.946-5.36 11.949-11.948a11.87 11.87 0 00-3.497-8.422h.12z"
      fill="#2F3C53"
    />
  </svg>
);

const ShareForm = ({ listing, triggerLocation }: ShareFormProps) => {
  const [copyText, setCopyText] = useState('Copy Link');

  const copyLinkUrl = buildShareUrl(listing, 'copy_link', 'referral');
  const handleCopyLink = useCallback(() => {
    emitTrackHdpCopyShareUrlEvent({ triggerLocation });
    navigator.clipboard
      .writeText(copyLinkUrl)
      .then(() => {
        setCopyText('Copied!');
        setTimeout(() => setCopyText('Copy Link'), 2000);
      })
      .catch((err) => {
        logError({ error: err, context: 'Error copying share URL to clipboard' });
      });
  }, [copyLinkUrl]);

  const smsUrl = buildShareUrl(listing, 'sms', 'sms');
  const smsHref = `sms:?body=${encodeURIComponent(`${SHARE_SUBJECT} ${smsUrl}`)}`;
  const messengerUrl = buildShareUrl(listing, 'messenger', 'social');
  const messengerHref = `https://www.facebook.com/dialog/send?link=${encodeURIComponent(messengerUrl)}&redirect_uri=${encodeURIComponent(messengerUrl)}`;
  const whatsappUrl = buildShareUrl(listing, 'whatsapp', 'social');
  const whatsappHref = `https://api.whatsapp.com/send?text=${encodeURIComponent(`${SHARE_SUBJECT} ${whatsappUrl}`)}`;
  const facebookUrl = buildShareUrl(listing, 'facebook', 'social');
  const facebookHref = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(facebookUrl)}`;
  const twitterUrl = buildShareUrl(listing, 'twitter', 'social');
  const twitterHref = `https://twitter.com/intent/tweet?text=${encodeURIComponent(`${SHARE_SUBJECT} ${twitterUrl}`)}`;

  return (
    <div className={shareFormContainerCX} data-testid="share-form">
      <h2 className={shareFormTitleCX}>Share Home</h2>

      <ul className={socialListCX}>
        <li>
          <button type="button" className={socialItemCX} onClick={handleCopyLink} data-testid="share-copy-link">
            <img className={socialIconCX} src={IconShareLink} width="24" height="24" alt="" aria-hidden="true" />
            {copyText}
          </button>
        </li>
        <li>
          <a
            className={socialItemCX}
            href={smsHref}
            onClick={() => emitTrackHdpTextShareEvent({ triggerLocation })}
            data-testid="share-sms"
          >
            <img className={socialIconCX} src={IconShareSms} width="24" height="24" alt="" aria-hidden="true" />
            SMS
          </a>
        </li>
        <li>
          <a
            className={socialItemCX}
            href={messengerHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="share-messenger"
          >
            <MessengerIcon />
            Messenger
          </a>
        </li>
        <li>
          <a
            className={socialItemCX}
            href={whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="share-whatsapp"
          >
            <WhatsAppIcon />
            WhatsApp
          </a>
        </li>
        <li>
          <a
            className={socialItemCX}
            href={facebookHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="share-facebook"
          >
            <img className={socialIconCX} src={IconShareFacebook} width="24" height="24" alt="" aria-hidden="true" />
            Facebook
          </a>
        </li>
        <li>
          <a
            className={socialItemCX}
            href={twitterHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="share-twitter"
          >
            <img className={socialIconCX} src={IconShareTwitter} width="24" height="24" alt="" aria-hidden="true" />
            Twitter
          </a>
        </li>
      </ul>
    </div>
  );
};

export default ShareForm;
